import {motion} from 'framer-motion';
import {useState,useEffect} from 'react';
import {Link,useNavigate} from 'react-router-dom';
import {useLanguage} from '../context/LanguageContext';
import {FaTools,FaGraduationCap,FaCalendarCheck,FaArrowRight,FaChartLine,FaClipboardList,FaUsers,FaComments,FaBrain,FaHeartbeat,FaShieldAlt,FaAngleDown,FaAngleUp,FaRobot,FaExternalLinkAlt,FaTimes,FaChartBar,FaChartPie,FaLightbulb,FaImage,FaPlay,FaBook,FaCoffee,FaClock,FaBullseye,FaCogs,FaUserMd,FaInfoCircle,FaEye,FaVideo,FaDesktop,FaPlayCircle,FaSchool,FaHospital} from 'react-icons/fa';
import {SiOpenai} from 'react-icons/si';
import SafeIcon from '../common/SafeIcon';
import CosmoHeads from '../components/CosmoHeads';
import CosmoAttentionLevels from '../components/CosmoAttentionLevels';
import VideoModal from '../components/VideoModal';
import FAQSection from '../components/FAQSection';
import {usePodcastReset} from '../hooks/usePodcastManager';

const ServiceCard=({title,description,icon,buttonText,buttonLink,bgColor,delay,imageSrc,customButtons=null,hasVideo=false,onVideoClick=null})=> {
  const Icon=icon;
  return (
    <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay,duration: 0.5}} className={`${bgColor} rounded-xl shadow-xl overflow-hidden relative flex flex-col w-full h-full`} >
      {/* Background Image for Services */}
      {imageSrc && (
        <div className="absolute inset-0 bg-cover bg-center opacity-35" style={{backgroundImage: `url(${imageSrc})`}} />
      )}
      <div className="relative z-10 p-8 flex-1 flex flex-col">
        {/* Titolo con Video Button se necessario */}
        <div className="flex items-center justify-between mb-6">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
            <Icon className="text-white text-3xl" />
          </div>
          {/* Video Button in alto a destra se hasVideo è true */}
          {hasVideo && (
            <motion.button onClick={onVideoClick} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}} className="flex items-center justify-center w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full transition-all duration-150" title="Watch Video Introduction" >
              <FaPlay className="text-sm ml-0.5" />
            </motion.button>
          )}
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
        <p className="text-white/90 mb-6 flex-grow">{description}</p>
        <div className="mt-auto">
          {customButtons ? (
            customButtons
          ) : (
            buttonLink.startsWith('#') ? (
              <Link to={buttonLink.substring(1)} className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md text-center justify-center w-full sm:w-auto" >
                {buttonText} <FaArrowRight className="ml-2" />
              </Link>
            ) : buttonLink.startsWith('/') ? (
              <Link to={buttonLink} className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md text-center justify-center w-full sm:w-auto" >
                {buttonText} <FaArrowRight className="ml-2" />
              </Link>
            ) : (
              <a href={buttonLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md text-center justify-center w-full sm:w-auto" >
                {buttonText} <FaArrowRight className="ml-2" />
              </a>
            )
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Video Modal Component for Self Report Tools
const SelfReportVideoModal=({isOpen,onClose})=> {
  const {language}=useLanguage();
  if (!isOpen) return null;
  // Video URLs per lingua
  const videoUrls={
    en: "https://app.heygen.com/embeds/5ac7e359e5044c46a263b0c084574299",
    it: "https://app.heygen.com/embeds/c60380026ae447a98f06174ba8c671d5"
  };
  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose} >
      <motion.div initial={{scale: 0.8,opacity: 0}} animate={{scale: 1,opacity: 1}} exit={{scale: 0.8,opacity: 0}} className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden" onClick={(e)=> e.stopPropagation()} >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {language==='en' ? 'Self Report Tools - Video Introduction' : 'Strumenti di Autovalutazione - Video Introduzione'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
          <iframe src={videoUrls[language]} title="HeyGen video player" frameBorder="0" allow="encrypted-media;fullscreen;" allowFullScreen className="absolute top-0 left-0 w-full h-full" />
        </div>
      </motion.div>
    </motion.div>
  );
};

// Video Modal Component for Typical Performance Tools
const TypicalPerformanceVideoModal=({isOpen,onClose})=> {
  const {language}=useLanguage();
  if (!isOpen) return null;
  // Video URLs per lingua
  const videoUrls={
    en: "https://app.heygen.com/embeds/082cae9ee6e64fc5848dc32d0b07f45e",
    it: "https://app.heygen.com/embeds/c7738c0daba34550964700bbad2560c1"
  };
  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose} >
      <motion.div initial={{scale: 0.8,opacity: 0}} animate={{scale: 1,opacity: 1}} exit={{scale: 0.8,opacity: 0}} className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden" onClick={(e)=> e.stopPropagation()} >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {language==='en' ? 'Typical Performance Tools - Video Introduction' : 'Strumenti di Performance Tipica - Video Introduzione'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
          <iframe src={videoUrls[language]} title="HeyGen video player" frameBorder="0" allow="encrypted-media;fullscreen;" allowFullScreen className="absolute top-0 left-0 w-full h-full" />
        </div>
      </motion.div>
    </motion.div>
  );
};

// Dashboard Video Modal
const DashboardVideoModal=({isOpen,onClose})=> {
  const {language}=useLanguage();
  if (!isOpen) return null;
  // Video URLs per lingua
  const videoUrls={
    en: "https://app.heygen.com/embeds/5fab3431fe2745b0a2fb50adc1b56645",
    it: "https://app.heygen.com/embeds/6b284e93fa5b4665b24c286134d4db2e"
  };
  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose} >
      <motion.div initial={{scale: 0.8,opacity: 0}} animate={{scale: 1,opacity: 1}} exit={{scale: 0.8,opacity: 0}} className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden" onClick={(e)=> e.stopPropagation()} >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {language==='en' ? 'COSMO Dashboard Explained' : 'Spiegazione Dashboard COSMO'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
          <iframe src={videoUrls[language]} title="HeyGen video player" frameBorder="0" allow="encrypted-media;fullscreen;" allowFullScreen className="absolute top-0 left-0 w-full h-full" />
        </div>
      </motion.div>
    </motion.div>
  );
};

// Modal per il video di Consultazione Professionale
const ConsultationVideoModal=({isOpen,onClose})=> {
  const {language}=useLanguage();
  if (!isOpen) return null;
  const videoUrls={
    en: "https://app.heygen.com/embeds/d965a048a5634a8da336351c63481ab4",
    it: "https://app.heygen.com/embeds/29c26f1cc66e404898ed52d67481224a"
  };
  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose} >
      <motion.div initial={{scale: 0.8,opacity: 0}} animate={{scale: 1,opacity: 1}} exit={{scale: 0.8,opacity: 0}} className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden" onClick={(e)=> e.stopPropagation()} >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {language==='en' ? 'Professional Test Scoring Support' : 'Supporto Professionale allo Scoring dei Test'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="relative w-full" style={{paddingBottom: '56.25%'}}>
          <iframe src={videoUrls[language]} title={language==='en' ? "Cosmo Support test scoring" : "Cosmo Support test scoring ITA"} frameBorder="0" allow="encrypted-media;fullscreen;" allowFullScreen className="absolute top-0 left-0 w-full h-full" />
        </div>
      </motion.div>
    </motion.div>
  );
};

const ToolCategoryCard=({title,description,icon,content,delay,imageSrc,hasVideo=false,onVideoClick=null,tooltip=null})=> {
  const [expanded,setExpanded]=useState(false);
  const {language}=useLanguage();
  return (
    <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay,duration: 0.5}} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100" >
      <div className="p-6">
        {/* Header Section con immagine pulita */}
        <div className="flex flex-col items-center mb-5">
          <div className="w-56 h-56 rounded-lg overflow-hidden mb-6">
            <img src={imageSrc} alt={title} className="w-full h-full object-contain" />
          </div>
        </div>
        {/* Title Section con Video Button integrato a destra */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center group relative cursor-help">
              <h4 className="text-xl font-bold text-indigo-700">{title}</h4>
              {tooltip && (
                <>
                  <FaInfoCircle className="ml-2 text-indigo-400 text-sm" />
                  <div className="absolute top-full left-0 mt-2 hidden group-hover:block w-72 bg-gray-800 text-white text-sm rounded-lg py-2 px-4 z-50 font-normal shadow-xl">
                    {tooltip}
                    <div className="absolute bottom-full left-6 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-gray-800"></div>
                  </div>
                </>
              )}
            </div>
            {/* Video Button compatto a destra del titolo */}
            {hasVideo && (
              <motion.button onClick={onVideoClick} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}} className="ml-4 flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-150 border-2 border-white hover:shadow-xl" title={language==='en' ? 'Watch Video Introduction' : 'Guarda Video Introduzione'} >
                <FaPlay className="text-sm ml-0.5" />
              </motion.button>
            )}
          </div>
          <p className="text-gray-700 font-medium">{description}</p>
        </div>
        {/* Solo Learn More Button */}
        <div className="flex flex-col gap-3 mb-4">
          <button onClick={()=> setExpanded(!expanded)} className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 text-indigo-700 rounded-lg font-semibold transition-all border border-indigo-200 hover:border-indigo-300" >
            <FaEye className="text-lg" />
            <span>
              {expanded ? (language==='en' ? 'Show Less' : 'Mostra Meno') : (language==='en' ? 'Learn More' : 'Scopri di Più')}
            </span>
            {expanded ? (
              <FaAngleUp className="text-lg" />
            ) : (
              <FaAngleDown className="text-lg" />
            )}
          </button>
        </div>
      </div>
      {expanded && (
        <motion.div initial={{opacity: 0,height: 0}} animate={{opacity: 1,height: "auto"}} exit={{opacity: 0,height: 0}} className="border-t border-gray-100 bg-gray-50" >
          <div className="p-6 prose prose-indigo max-w-none">
            {content}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Compact Mission Boxes
const CompactMissionBoxes=()=> {
  const {language}=useLanguage();
  const [activeTooltip,setActiveTooltip]=useState(null);
  const missionItems=[
    {
      id: 'mission',
      icon: FaBullseye,
      title: language==='en' ? 'Our Mission' : 'La Nostra Missione',
      color: 'bg-indigo-600',
      hoverColor: 'hover:bg-indigo-700',
      description: language==='en' ? 'Our mission is to help psychologists integrate clinical judgment with data-driven insights,elevating assessment and empowering them with comprehensive,multi-method evaluation tools to deliver more effective treatment.' : 'La nostra missione è aiutare gli psicologi a integrare il giudizio clinico con insights basati sui dati,elevando la valutazione e fornendo loro strumenti di valutazione completi e multi-metodo per offrire trattamenti più efficaci.'
    },
    {
      id: 'why-choose',
      icon: FaUsers,
      title: language==='en' ? 'Why Choose COSMO?' : 'Perché Scegliere COSMO?',
      color: 'bg-green-600',
      hoverColor: 'hover:bg-green-700',
      description: language==='en' ? 'With our platform,psychologists can select the most suitable assessment tools and easily enter client data. An intuitive,interactive dashboard displays results with clear graphs and comparisons across multiple tests,providing a complete 360° view of each case. Adding unique value,two of our COSMO team members are the authors of the performance-based tests included—ensuring deep expertise and direct insight into these instruments.' : 'Con la nostra piattaforma,gli psicologi possono selezionare gli strumenti di valutazione più adatti e inserire facilmente i dati del cliente. Una dashboard intuitiva e interattiva mostra i risultati con grafici chiari e confronti tra più test,fornendo una visione completa a 360° di ogni caso. Aggiungendo valore unico,due membri del nostro team COSMO sono gli autori dei test basati sulle prestazioni inclusi—garantendo competenza approfondita e intuizioni dirette su questi strumenti.'
    },
    {
      id: 'how-it-works',
      icon: FaCogs,
      title: language==='en' ? 'How It Works' : 'Come Funziona',
      color: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700',
      description: language==='en' ? 'With our platform,psychologists can select the most suitable assessment tools and easily enter client data. An intuitive,interactive dashboard displays results with clear graphs and comparisons across multiple tests,providing a complete 360° view of each case. Professionals can also download comprehensive reports' : 'Con la nostra piattaforma,gli psicologi possono selezionare gli strumenti di valutazione più adatti e inserire facilmente i dati del cliente. Una dashboard intuitiva e interattiva mostra i risultati con grafici chiari e confronti tra più test,fornendo una visione completa a 360° di ogni caso. I professionisti possono anche scaricare report completi'
    }
  ];
  return (
    <div className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {missionItems.map((item,index)=> (
            <motion.div key={item.id} initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: index * 0.1,duration: 0.5}} className="relative" onMouseEnter={()=> setActiveTooltip(item.id)} onMouseLeave={()=> setActiveTooltip(null)} >
              <div className={`${item.color} ${item.hoverColor} rounded-xl p-6 text-white cursor-pointer transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl`}>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <div className="flex items-center justify-center text-white/80">
                    <FaInfoCircle className="text-sm mr-1" />
                    <span className="text-sm">
                      {language==='en' ? 'Hover for details' : 'Passa sopra per dettagli'}
                    </span>
                  </div>
                </div>
              </div>
              {/* Tooltip */}
              {activeTooltip===item.id && (
                <motion.div initial={{opacity: 0,y: 10}} animate={{opacity: 1,y: 0}} className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-4" >
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {item.description}
                  </div>
                  {/* Arrow */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GPTSection=()=> {
  const {language}=useLanguage();
  const gptAssistants=[
    {
      name: 'COSMO GPT',
      description: language==='en' ? 'Supports the new COSMO coding system for Picture Frustration Study assessment' : 'Supporta il nuovo sistema di codifica COSMO per la valutazione Picture Frustration Study',
      url: 'https://chatgpt.com/g/g-u2Gz3Kz4r-cosmo',
      available: true
    },
    {
      name: 'LPF Scale 2.0 BF',
      description: language==='en' ? 'Specialized assistant for LPF Scale assessment' : 'Assistente specializzato per valutazione LPF Scale',
      url: 'https://chatgpt.com/g/g-E8O5OKH86-lpf-scale-2-0-bf',
      available: true
    },
    {
      name: 'Compass of Shame',
      description: language==='en' ? 'Expert guidance for shame assessment' : 'Guida esperta per valutazione della vergogna',
      url: 'https://chatgpt.com/g/g-GtESbQezP-compass-of-shame',
      available: true
    },
    {
      name: 'Epistemic Trust',
      description: language==='en' ? 'Specialized support for epistemic trust' : 'Supporto specializzato per fiducia epistemica',
      url: 'https://chatgpt.com/g/g-fkhOROjIs-epistemic-trust-mistrust-credulity-questionnaire',
      available: true
    },
    {
      name: 'Reflective Functioning',
      description: language==='en' ? 'Assistant for reflective functioning assessment' : 'Assistente per valutazione funzionamento riflessivo',
      url: 'https://chatgpt.com/g/g-67399454beb48191833d0af07b6aa15d-reflective-functioning-questionnaire',
      available: true
    },
    {
      name: 'PID-5-BF',
      description: language==='en' ? 'Assistant for PID-5 Brief Form assessment' : 'Assistente per valutazione PID-5 Brief Form',
      url: 'https://chatgpt.com/g/g-678ac82f58dc8191aa0b89252c65d8ea-pid-5-bf',
      available: true
    },
    {
      name: 'Dissociative Experiences Scale',
      description: language==='en' ? 'Specialized assistant for dissociative experiences assessment' : 'Assistente specializzato per valutazione esperienze dissociative',
      url: 'https://chatgpt.com/g/g-6842081e08388191aa3701d84edcf646-des',
      available: true
    },
    {
      name: 'Therapeutic Alliance',
      description: language==='en' ? 'Assistant for therapeutic alliance assessment' : 'Assistente per valutazione alleanza terapeutica',
      url: 'https://chatgpt.com/g/g-673a584f49788191a24ec4f9bb2d2509-working-alliance',
      available: true
    }
  ];

  const handleAssistantClick=(assistant)=> {
    if (assistant.available && assistant.url !=='#') {
      window.open(assistant.url,'_blank','noopener,noreferrer');
    }
  };

  const BuyCoffeeButton=()=> {
    return (
      <a href="https://buy.stripe.com/cN24j57Uh55EdHOfZ4" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium px-4 py-3 rounded-lg shadow-md hover:shadow-lg transition-all transform hover:scale-105 w-full justify-center" >
        <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750973984906-Mascotte_testa_1_to_1.png" alt="COSMO mascot" className="w-5 h-5 rounded-full" />
        <FaCoffee className="text-lg" />
        <span className="text-sm font-semibold">
          {language==='en' ? 'Buy COSMO a Coffee' : 'Offri un Caffè a COSMO'}
        </span>
      </a>
    );
  };

  return (
    <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.6}} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200" >
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
          <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750973984906-Mascotte_testa_1_to_1.png" alt="COSMO" className="w-12 h-12 rounded-full" />
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
            <SiOpenai className="text-green-600 text-xs" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {language==='en' ? 'COSMO AI Assistants' : 'Assistenti AI COSMO'}
        </h3>
        <p className="text-gray-600 max-w-2xl mx-auto mb-2">
          {language==='en' ? 'Interact with our specialized AI assistants trained on specific personality assessment tools to get instant guidance,interpretation help,and training support.' : 'Interagisci con i nostri assistenti AI specializzati addestrati su strumenti di valutazione della personalità specifici per ottenere guida istantanea,aiuto nell\'interpretazione e supporto formativo.'}
        </p>
        <p className="text-sm text-gray-500 flex items-center justify-center">
          <span className="mr-2">Powered by</span>
          <SiOpenai className="text-gray-600 mr-1" />
          <span className="font-medium">OpenAI</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {gptAssistants.map((assistant,index)=> (
          <div key={index} className="group rounded-lg p-4 shadow-md transition-all duration-300 border transform bg-white border-green-200 hover:border-green-300 hover:shadow-xl cursor-pointer hover:scale-105 hover:bg-green-50" onClick={()=> handleAssistantClick(assistant)} >
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 relative overflow-hidden bg-green-100 group-hover:bg-green-200">
                <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750973984906-Mascotte_testa_1_to_1.png" alt="COSMO" className="w-8 h-8 rounded-full object-cover" />
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <SiOpenai className="text-xs text-green-600" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm transition-colors text-gray-900 group-hover:text-green-700">
                  {assistant.name}
                </h4>
                <div className="flex items-center mt-1">
                  <FaExternalLinkAlt className="text-green-500 text-xs mr-1" />
                  <span className="text-xs text-green-600 font-medium">
                    {language==='en' ? 'Available' : 'Disponibile'}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs leading-relaxed text-gray-600 group-hover:text-gray-700">
              {assistant.description}
            </p>
            <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="text-xs text-green-600 font-medium flex items-center">
                <span className="mr-1">→</span>
                {language==='en' ? 'Click to open ChatGPT' : 'Clicca per aprire ChatGPT'}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="text-amber-800 text-sm lg:max-w-[65%] text-center lg:text-left">
            <strong>
              {language==='en' ? 'Support the COSMO platform with a small donation. Help us keep our personality assessment tools and scientific resources free for everyone.' : 'Sostieni la piattaforma COSMO con una piccola donazione. Aiutaci a mantenere i nostri strumenti di valutazione della personalità e le risorse scientifiche gratuite per tutti.'}
            </strong>
          </div>
          <div className="lg:max-w-[35%] w-full lg:w-auto">
            <BuyCoffeeButton />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const InteractiveDashboardShowcase=()=> {
  const {language}=useLanguage();
  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="text-center mb-12" >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {language==='en' ? 'Interactive Dashboard' : 'Dashboard Interattiva'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {language==='en' ? 'While other platforms offer isolated test results,COSMO provides the only comprehensive system that integrates multiple personality assessment methodologies.' : 'Mentre altre piattaforme offrono risultati di test isolati,COSMO fornisce l\'unico sistema completo che integra più metodologie di valutazione della personalità.'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.3 + 0 * 0.1,duration: 0.5}} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow" >
            <div className="text-4xl mb-4 text-indigo-600">
              <FaChartPie className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              {language==='en' ? 'Cross-Test Comparison' : 'Confronto Tra Test'}
            </h3>
            <p className="text-gray-600 text-center">
              {language==='en' ? 'Compare different personality assessment tools side-by-side for a comprehensive view of your patient\'s personality profile.' : 'Confronta diversi strumenti di valutazione della personalità fianco a fianco per una visione completa del profilo di personalità del paziente.'}
            </p>
          </motion.div>

          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.3 + 1 * 0.1,duration: 0.5}} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow" >
            <div className="text-4xl mb-4 text-green-600">
              <FaChartBar className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              {language==='en' ? 'Multi-Method Integration' : 'Integrazione Multi-Metodo'}
            </h3>
            <p className="text-gray-600 text-center">
              {language==='en' ? 'Integrate self-report and performance assessments to reveal hidden patterns between personality aspects.' : 'Integra valutazioni auto-riportate e di performance per rivelare modelli nascosti tra aspetti della personalità.'}
            </p>
          </motion.div>

          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.3 + 2 * 0.1,duration: 0.5}} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow" >
            <div className="text-4xl mb-4 text-purple-600">
              <FaLightbulb className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              {language==='en' ? 'Clinical Decision Support' : 'Supporto alle Decisioni Cliniche'}
            </h3>
            <p className="text-gray-600 text-center">
              {language==='en' ? 'Enhance clinical judgment with data-driven insights for more effective and personalized treatment planning.' : 'Migliora il giudizio clinico con approfondimenti basati sui dati per una pianificazione del trattamento più efficace e personalizzata.'}
            </p>
          </motion.div>
        </div>

        <div className="mb-8">
          <CosmoAttentionLevels />
        </div>

        <div className="flex flex-col items-center justify-center py-6">
          <a href="https://cosmo-self-report-se-absv.bolt.host" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-4 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl mb-3" >
            <FaChartLine className="mr-3" />
            {language==='en' ? 'Try our Tools' : 'Prova i Nostri Strumenti'}
          </a>
        </div>
      </div>
    </div>
  );
};

const LandingPage=()=> {
  const {t,language}=useLanguage();
  const [showVideoModal,setShowVideoModal]=useState(false);
  const [showSelfReportVideoModal,setShowSelfReportVideoModal]=useState(false);
  const [showTypicalPerformanceVideoModal,setShowTypicalPerformanceVideoModal]=useState(false);
  const [showDashboardVideoModal,setShowDashboardVideoModal]=useState(false);
  const [showConsultationVideoModal,setShowConsultationVideoModal]=useState(false);
  const navigate=useNavigate();

  usePodcastReset();

  const handleNavigation=(path)=> {
    navigate(path);
    window.scrollTo(0,0);
  };

  const services=[
    {
      id: 1,
      title: language==='en' ? 'COSMO Assessment Tools' : 'Strumenti di Valutazione COSMO',
      description: language==='en' ? 'Elevate your practice through multimethod personality assessment tools designed to provide rich insights into your clients.' : 'Migliora la tua pratica con strumenti di valutazione della personalità multimodali progettati per fornire approfondimenti sui tuoi clienti.',
      icon: FaTools,
      buttonText: language==='en' ? 'Try our Tools' : 'Prova i Nostri Strumenti',
      buttonLink: 'https://cosmo-self-report-se-absv.bolt.host',
      bgColor: 'bg-indigo-700',
      imageSrc: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753518636041-03.png'
    },
    {
      id: 2,
      title: language==='en' ? 'COSMO Education' : 'COSMO Formazione',
      description: language==='en' ? 'Access premium courses and free educational resources for personality assessment professionals.' : 'Accedi a corsi premium e risorse educative gratuite per professionisti della valutazione della personalità.',
      icon: FaGraduationCap,
      bgColor: 'bg-green-700',
      imageSrc: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753519422928-09.png',
      customButtons: (
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={()=> handleNavigation("/courses")} className="inline-flex items-center justify-center px-4 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md w-full" >
            <FaGraduationCap className="mr-2" />
            {language==='en' ? 'Courses' : 'Corsi'}
          </button>
          <button onClick={()=> handleNavigation("/free-resources")} className="inline-flex items-center justify-center px-4 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md w-full" >
            <FaBook className="mr-2" />
            {language==='en' ? 'Articles' : 'Articoli'}
          </button>
        </div>
      )
    },
    {
      id: 3,
      title: language==='en' ? 'Professional Consultations' : 'Consulenze Professionali',
      description: language==='en' ? 'Schedule personalized consultations with our expert psychologists for personality assessment and therapy guidance.' : 'Prenota consulenze personalizzate con i nostri psicologi esperti per valutazioni della personalità e indicazioni terapeutiche.',
      icon: FaCalendarCheck,
      buttonText: language==='en' ? 'Contact Our Team' : 'Contatta il Nostro Team',
      buttonLink: 'https://cosmopersonality.trafft.com',
      bgColor: 'bg-purple-700',
      imageSrc: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753517572437-04.png',
      hasVideo: true,
      onVideoClick: ()=> setShowConsultationVideoModal(true)
    },
    {
      id: 4,
      title: language==='en' ? 'COSMO Schools' : 'COSMO Scuole',
      description: language==='en' ? 'Comprehensive institutional support for educational institutions. Implement COSMO assessment tools and training programs to enhance student psychological evaluation and support services within schools.' : 'Supporto istituzionale completo per le istituzioni educative. Implementa strumenti di valutazione COSMO e programmi di formazione per migliorare la valutazione psicologica degli studenti e i servizi di supporto nelle scuole.',
      icon: FaSchool,
      buttonText: language==='en' ? 'Discover More' : 'Scopri di Più',
      buttonLink: '/schools',
      bgColor: 'bg-orange-600',
      imageSrc: 'https://media-manager-c.questera.ai/greta-media/97cec6254316a951f824fbca4347b65f7f1a7e8bfb3213050d69d6b9d996aba3dd0371de97ad8eb7d82529d579c85e3f/images/aW1hZ2UvcG5n/8285e8ce6e307e563b30eed6e0f0d2d3.png'
    },
    {
      id: 5,
      title: language==='en' ? 'COSMO Treatment Centers' : 'COSMO Centri di Trattamento',
      description: language==='en' ? 'Specialized institutional support for treatment centers and mental health facilities. Integrate COSMO\'s multi-method assessment platform to enhance clinical decision-making and patient care quality.' : 'Supporto istituzionale specializzato per centri di trattamento e structures di salute mentale. Integra la piattaforma di valutazione multi-metodo di COSMO per migliorare il processo decisionale clinico e la qualità delle cure dei pazienti.',
      icon: FaHospital,
      buttonText: language==='en' ? 'Discover More' : 'Scopri di Più',
      buttonLink: '/treatment-centers',
      bgColor: 'bg-teal-600',
      imageSrc: 'https://media-manager-c.questera.ai/greta-media/97cec6254316a951f824fbca4347b65f7f1a7e8bfb3213050d69d6b9d996aba3dd0371de97ad8eb7d82529d579c85e3f/images/aW1hZ2UvcG5n/06162aab33841d1cb736e343b9f79ec0.png'
    }
  ];

  const selfReportToolsContent=language==='en' ? (
    <>
      <h3 className="text-xl font-bold text-indigo-900 mb-4"> Welcome to the COSMO Self-Report Tools Section </h3>
      <p className="mb-4"> The COSMO Self-Report Tools section is designed to enhance your clinical practice by providing high-quality data and targeted insights to support informed clinical decision-making. With COSMO Self-Report Tools,you have access to a range of assessments crafted to provide a deep understanding of various aspects of your patients' personality experiences. These tools cover multiple areas,including: </p>
      <ul className="list-disc pl-5 mb-4">
        <li><strong>Severity:</strong> to assess the intensity and urgency of personality symptoms.</li>
        <li><strong>Personality Traits:</strong> to outline key characteristics of the patient's personality.</li>
        <li><strong>Epistemic Trust:</strong> to evaluate the patient's openness to new information in social contexts.</li>
        <li><strong>Shame:</strong> to explore hidden feelings of shame that may hinder therapeutic progress.</li>
        <li><strong>Therapeutic Alliance:</strong> to monitor the quality of the therapeutic relationship and patient engagement.</li>
        <li><strong>Dissociative Experiences:</strong> to assess dissociative symptoms and experiences.</li>
      </ul>
      <p className="mb-4"> <strong>Key personality self-report tools available on our platform:</strong> </p>
      <ul className="list-disc pl-5 mb-4">
        <li>LPF Scale 2.0 Brief Form | Interact with the COSMO GPT</li>
        <li>Epistemic Trust Questionnaire | Interact with the COSMO GPT</li>
        <li>Reflective Functioning Questionnaire | Interact with the COSMO GPT</li>
        <li>Compass of Shame | Interact with the COSMO GPT</li>
        <li>PID 5 Brief Form Plus | Interact with the COSMO GPT</li>
        <li>Dissociative Experiences Scale | Interact with the COSMO GPT</li>
        <li>Therapeutic Alliance | Interact with the COSMO GPT</li>
      </ul>
      <p className="mb-4"> To support your use of these tools,COSMO provides comprehensive video courses for each assessment,guiding you step-by-step to ensure optimal use and interpretation. </p>
      <p className="mb-4"> Our intuitive dashboard also allows you to compare different personality tests side by side,providing a complete and cohesive view of your patient's personality condition. You can select specific assessments to compare,offering a richer context for more informed decision-making. </p>
      <p className="mb-4"> Additionally,for each personality assessment tool,we provide a dedicated GPT model specifically trained to assist with training,interpreting results,and delving deeper into the insights provided by each tool. </p>
      <p> Whether you work in private practice,a treatment center,or a larger organization,COSMO's self-report tools are tailored to meet your specific needs. Our goal is to streamline your personality assessments,enabling you to provide the highest quality support to your patients. </p>
    </>
  ) : (
    <>
      <h3 className="text-xl font-bold text-indigo-900 mb-4"> Benvenuti nella sezione Strumenti di Autovalutazione COSMO </h3>
      <p className="mb-4"> La sezione Strumenti di Autovalutazione COSMO è progettata per migliorare la tua pratica clinica fornendo dati di alta qualità e approfondimenti mirati per supportare decisioni cliniche informate. Con gli Strumenti di Autovalutazione COSMO,hai accesso a una gamma di valutazioni create per fornire una comprensione profonda di vari aspetti delle esperienze di personalità dei tuoi pazienti. Questi strumenti coprono diverse aree,tra cui: </p>
      <ul className="list-disc pl-5 mb-4">
        <li><strong>Gravità:</strong> per valutare l'intensità e l'urgenza dei sintomi di personalità.</li>
        <li><strong>Tratti di Personalità:</strong> per delineare le caratteristiche chiave della personalità del paziente.</li>
        <li><strong>Fiducia Epistemica:</strong> per valutare l'apertura del paziente a nuove informazioni in contesti sociali.</li>
        <li><strong>Vergogna:</strong> per esplorare sentimenti nascosti di vergogna che potrebbero ostacolare il progresso terapeutico.</li>
        <li><strong>Alleanza Terapeutica:</strong> per monitorare la qualità della relazione terapeutica e il coinvolgimento del paziente.</li>
        <li><strong>Esperienze Dissociative:</strong> per valutare sintomi ed esperienze dissociative.</li>
      </ul>
      <p className="mb-4"> <strong>Principali strumenti di autovalutazione per la personalità disponibili sulla nostra piattaforma:</strong> </p>
      <ul className="list-disc pl-5 mb-4">
        <li>LPF Scale 2.0 Brief Form | Interagisci con il COSMO GPT</li>
        <li>Questionario sulla Fiducia Epistemica | Interagisci con il COSMO GPT</li>
        <li>Questionario sul Funzionamento Riflessivo | Interagisci con il COSMO GPT</li>
        <li>Bussola della Vergogna | Interagisci con il COSMO GPT</li>
        <li>PID 5 Brief Form Plus | Interagisci con il COSMO GPT</li>
        <li>Scala delle Esperienze Dissociative | Interagisci con il COSMO GPT</li>
        <li>Alleanza Terapeutica | Interagisci con il COSMO GPT</li>
      </ul>
      <p className="mb-4"> Per supportare l'utilizzo di questi strumenti,COSMO fornisce corsi video completi per ogni valutazione,guidandoti passo dopo passo per garantire un uso e un'interpretazione ottimali. </p>
      <p className="mb-4"> La nostra dashboard intuitiva ti permette anche di confrontare diversi test di personalità fianco a fianco,fornendo una visione completa e coesa della condizione di personalità del tuo paziente. Puoi selezionare valutazioni specifiche da confrontare,offrendo un contesto più ricco per decisioni più informate. </p>
      <p className="mb-4"> Inoltre,per ogni strumento di valutazione della personalità,forniamo un modello GPT dedicato specificamente addestrato per assistere nella formazione,nell'interpretazione dei risultati e nell'approfondimento delle intuizioni fornite da ciascuno strumento. </p>
      <p> Che tu lavori in uno studio privato,un centro di trattamento o un'organizzazione più grande,gli strumenti di autovalutazione COSMO sono progettati per soddisfare le tue esigenze specifiche. Il nostro obiettivo è semplificare le tue valutazioni della personalità,permettendoti di fornire il supporto di massima qualità ai tuoi pazienti. </p>
    </>
  );

  const typicalPerformanceToolsContent=language==='en' ? (
    <>
      <h3 className="text-xl font-bold text-indigo-900 mb-4"> Typical Performance Tools: An Innovative Approach to Personality Assessment </h3>
      <p className="mb-4"> COSMO's Typical Performance Tools section is designed to help professionals gain deeper insights into patients' relational dynamics and emotional defenses by integrating performance-based personality assessment tools. While self-report tests provide direct information about patients' experiences,performance-based tools such as the Picture Frustration Study (PFS) and the Thurston-Cradock Test of Shame (TCTS) reveal underlying aspects of personality that are often hidden or unconscious. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> COSMO's Coding System for the Rosenzweig Picture Frustration Study | Interact with the COSMO GPT </h4>
      <p className="mb-4"> The Picture Frustration Study (PFS),redefined by COSMO,is a projective-associative tool that uses slightly frustrating everyday situations to assess patients' relational and problem-solving skills. COSMO's innovative coding method categorizes responses based on interpersonal and coping strategies,focusing on key dimensions of personality functioning as defined in the DSM-5 Alternative Model of Personality Disorders (AMPD). Responses are mapped into four quadrants reflecting interpersonal style,from friendly/cooperative to hostile/dominant,allowing clinicians to observe adaptive and maladaptive patterns within social interactions. This structured approach enhances the clinical utility of the PFS,making it a powerful tool to identify specific relational styles and coping responses in patients. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> The Thurston-Cradock Test of Shame (TCTS) </h4>
      <p className="mb-4"> The TCTS,developed by Dr. Julie Cradock O'Leary,is a performance-based storytelling test that explores the complex,often hidden,dynamics of shame. Participants respond to ambiguous,shame-related images,and their narratives are analyzed for direct and indirect indicators of shame. The test categorizes defenses against shame into three main types —deflation,aggression,and contempt/inflation—alongside the severity of these defenses. This method provides clinicians with unique insights into a patient's inner experiences,revealing how deeply-rooted feelings of inadequacy may impact their behavior and relationships. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> Enhanced Clinical Insight through Typical Performance Tools </h4>
      <p className="mb-4"> COSMO's Typical Performance Tools bridge the gap between self-perception and actual behavior in social contexts,providing a 360° view of personality dynamics. These tools are particularly effective within the framework of collaborative assessment,as advocated by Stephen Finn. This approach emphasizes the importance of using assessment as a tool for increasing self-awareness and fostering positive change,achieved through a collaborative relationship between clinician and patient. By combining these performance-based tools with self-report assessments,clinicians can achieve a comprehensive understanding of each patient's personality functioning,empowering them to deliver more targeted,effective interventions that align with each patient's unique therapeutic journey. </p>
    </>
  ) : (
    <>
      <h3 className="text-xl font-bold text-indigo-900 mb-4"> Strumenti di Performance Tipica: Un Approccio Innovativo alla Valutazione della Personalità </h3>
      <p className="mb-4"> La sezione Strumenti di Performance Tipica di COSMO è progettata per aiutare i professionisti a ottenere approfondimenti più profondi sulle dinamiche relazionali e le difese emotive dei pazienti integrando strumenti di valutazione della personalità basati sulle prestazioni. Mentre i test di autovalutazione forniscono informazioni dirette sulle esperienze dei pazienti,strumenti basati sulle prestazioni come il Picture Frustration Study (PFS) e il Thurston-Cradock Test of Shame (TCTS) rivelano aspetti sottostanti della personalità che sono spesso nascosti o inconsci. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> Il Sistema di Codifica COSMO per il Rosenzweig Picture Frustration Study | Interagisci con il COSMO GPT </h4>
      <p className="mb-4"> Il Picture Frustration Study (PFS),ridefinito da COSMO,è uno strumento proiettivo-associativo che utilizza situazioni quotidiane leggermente frustranti per valutare le capacità relazionali e di risoluzione dei problemi dei pazienti. Il metodo di codifica innovativo di COSMO categorizza le risposte in base alle strategie interpersonali e di coping,concentrandosi sulle dimensioni chiave del funzionamento della personalità come definito nel Modello Alternativo dei Disturbi di Personalità (AMPD) del DSM-5. Le risposte sono mappate in quattro quadranti che riflettono lo stile interpersonale,da amichevole/cooperativo a ostile/dominante,permettendo ai clinici di osservare modelli adattivi e disadattivi nelle interazioni sociali. Questo approccio strutturato migliora l'utilità clinica del PFS,rendendolo uno strumento potente per identificare stili relazionali specifici e risposte di coping nei pazienti. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> Il Thurston-Cradock Test of Shame (TCTS) </h4>
      <p className="mb-4"> Il TCTS,sviluppato dalla Dr.ssa Julie Cradock O'Leary,è un test narrativo basato sulle prestazioni che esplora le dinamiche complesse,spesso nascoste,della vergogna. I partecipanti rispondono a immagini ambigue legate alla vergogna,e le loro narrazioni vengono analizzate per indicatori diretti e indiretti di vergogna. Il test categorizza le difese contro la vergogna in tre tipi principali —deflazione,aggressione e disprezzo/inflazione— insieme alla gravità di queste difese. Questo metodo fornisce ai clinici approfondimenti unici sulle esperienze interiori di un paziente,rivelando come sentimenti profondamente radicati di inadeguatezza possano influenzare il loro comportamento e le loro relazioni. </p>
      <h4 className="text-lg font-bold text-indigo-800 mb-2"> Approfondimento Clinico Avanzato attraverso gli Strumenti di Performance Tipica </h4>
      <p className="mb-4"> Gli Strumenti di Performance Tipica di COSMO colmano il divario tra auto-percezione e comportamento effettivo nei contesti sociali,fornendo una visione a 360° delle dinamiche della personalità. Questi strumenti sono particolarmente efficaci nel quadro della valutazione collaborativa,come sostenuto da Stephen Finn. Questo approccio enfatizza l'importanza di utilizzare la valutazione come strumento per aumentare l'auto-consapevolezza e promuovere un cambiamento positivo,ottenuto attraverso una relazione collaborativa tra clinico e paziente. Combinando questi strumenti basati sulle prestazioni con valutazioni di autovalutazione,i clinici possono raggiungere una comprensione completa del funzionamento della personalità di ciascun paziente,permettendo loro di fornire interventi più mirati ed efficaci che si allineano con il percorso terapeutico unico di ciascun paziente. </p>
    </>
  );

  const toolCategories=[
    {
      title: language==='en' ? 'Self Report Tools' : 'Strumenti di Autovalutazione',
      description: language==='en' ? 'Use our graphical dashboard to support your clinical judgment and practice with high-quality personality data and targeted insights.' : 'Utilizza la nostra dashboard grafica per supportare il tuo giudizio clinico e la pratica con dati di personalità di alta qualità e approfondimenti mirati.',
      icon: FaClipboardList,
      content: selfReportToolsContent,
      imageSrc: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753513732490-01.png',
      hasVideo: true,
      tooltip: language==='en' ? '* All self-report tools remain completely free.' : '* Tutti gli strumenti di autovalutazione restano completamente gratuiti.'
    },
    {
      title: language==='en' ? 'Typical Performance Tools' : 'Strumenti di Performance Tipica',
      description: language==='en' ? 'Utilize tools crafted by COSMO to leverage deeper insight into client responses and behavioral patterns.' : 'Utilizza strumenti creati da COSMO per ottenere approfondimenti più profondi nelle risposte dei clienti e nei modelli comportamentali.',
      icon: FaChartLine,
      content: typicalPerformanceToolsContent,
      imageSrc: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753513744404-02.png',
      hasVideo: true
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-gray-900 via-indigo-900 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{opacity: 0,x: -20}} animate={{opacity: 1,x: 0}} transition={{duration: 0.7}} className="space-y-8" >
              <motion.div className="flex flex-col items-center space-y-2" initial={{opacity: 0}} animate={{opacity: 1}} transition={{delay: 0.2,duration: 0.7}} >
                <div className="flex items-center space-x-4">
                  <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750973984906-Mascotte_testa_1_to_1.png" alt="COSMO Personality Logo" className="h-16 w-16 rounded-full" />
                  <div className="text-center">
                    <h1 className="text-4xl md:text-6xl font-bold">COSMO</h1>
                    <h2 className="text-lg md:text-xl font-medium text-gray-300">Personality</h2>
                  </div>
                </div>
              </motion.div>
              <motion.h3 initial={{opacity: 0}} animate={{opacity: 1}} transition={{delay: 0.4,duration: 0.7}} className="text-2xl md:text-3xl font-semibold text-center" >
                {language==='en' ? 'The All-in-One Platform for Multi-Method Personality Assessment and Tailored Treatment Planning' : 'La Piattaforma All-in-One per la Valutazione della Personalità Multi-Metodo e la Pianificazione del Trattamento Personalizzato'}
              </motion.h3>
              <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.8,duration: 0.5}} className="flex flex-col items-center relative z-20" >
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center mb-4">
                  <button onClick={()=> setShowVideoModal(true)} className="relative inline-flex items-center justify-center px-6 py-3 bg-transparent border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-colors shadow-lg min-w-[200px] z-30" >
                    <FaPlay className="mr-2" />
                    {language==='en' ? 'Platform Overview' : 'Panoramica Piattaforma'}
                  </button>
                  <button onClick={()=> setShowDashboardVideoModal(true)} className="relative inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-colors shadow-lg min-w-[200px] z-30" >
                    <FaPlayCircle className="mr-2" />
                    {language==='en' ? 'Dashboard Explained' : 'Spiegazione Dashboard'}
                  </button>
                  <div className="relative group inline-block z-30">
                    <a href="https://cosmo-self-report-se-absv.bolt.host" target="_blank" rel="noopener noreferrer" className="relative inline-flex items-center justify-center px-6 py-3 bg-white text-gray-900 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg min-w-[200px] cursor-pointer text-center" style={{pointerEvents: 'auto'}} >
                      <FaChartLine className="mr-2 flex-shrink-0" />
                      <span>{language==='en' ? 'Register & Claim Welcome Credits' : 'Registrati e Ottieni Crediti'}</span>
                    </a>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 hidden group-hover:block w-max max-w-xs sm:max-w-sm bg-indigo-900/95 text-white text-sm font-medium rounded-lg py-3 px-4 shadow-xl backdrop-blur-sm border border-white/20 text-center pointer-events-none z-50">
                      💡 {language==='en' ? 'Initial credits unlock advanced charts for 5 protocols. All self-report tools remain completely free forever.' : 'I crediti iniziali sbloccano grafici avanzati per 5 protocolli. Tutti gli strumenti di autovalutazione restano sempre completamente gratuiti.'}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-indigo-900/95"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            <motion.div initial={{opacity: 0,scale: 0.9}} animate={{opacity: 1,scale: 1}} transition={{delay: 0.4,duration: 0.7}} className="relative flex justify-center" >
              {/* Full Body Mascot */}
              <div className="relative">
                <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753463323939-Mascotte_01.png" alt="COSMO Personality Mascot" className="h-96 w-auto object-contain filter drop-shadow-2xl" />
                {/* Floating elements around mascot */}
                <motion.div animate={{y: [-10,10,-10]}} transition={{duration: 3,repeat: Infinity}} className="absolute top-10 -left-8 bg-white/10 backdrop-blur-sm rounded-full p-3" >
                  <FaTools className="text-white text-xl" />
                </motion.div>
                <motion.div animate={{y: [10,-10,10]}} transition={{duration: 3,repeat: Infinity,delay: 1}} className="absolute top-32 -right-8 bg-white/10 backdrop-blur-sm rounded-full p-3" >
                  <FaChartLine className="text-white text-xl" />
                </motion.div>
                <motion.div animate={{y: [-5,5,-5]}} transition={{duration: 3,repeat: Infinity,delay: 0.5}} className="absolute bottom-20 -left-6 bg-white/10 backdrop-blur-sm rounded-full p-3" >
                  <FaGraduationCap className="text-white text-xl" />
                </motion.div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-green-500 blur-2xl opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-purple-500 blur-2xl opacity-20"></div>
            </motion.div>
          </div>
        </div>
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" fill="#ffffff">
            <path d="M0,96L60,80C120,64,240,32,360,32C480,32,600,64,720,69.3C840,75,960,53,1080,48C1200,43,1320,53,1380,58.7L1440,64L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <CompactMissionBoxes />

      {/* Services Section con dimensioni uniformi fisse */}
      <div className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.8,duration: 0.8}} className="text-center mb-16" >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language==='en' ? 'COSMO: Complete Solutions for Every Setting' : 'COSMO: Soluzioni Complete per Ogni Contesto'}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              {language==='en' ? 'From individual practitioners to large institutions,COSMO provides comprehensive personality assessment solutions tailored to your specific needs and professional context.' : 'Dai professionisti individuali alle grandi istituzioni,COSMO fornisce soluzioni complete di valutazione della personalità su misura per le tue esigenze specifiche e il contesto professionale.'}
            </p>
          </motion.div>

          {/* Grid CSS con dimensioni fisse per uniformità perfetta */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Prima riga: 3 box */}
            {services.slice(0,3).map((service,index)=> (
              <motion.div key={service.id} initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.2 + index * 0.1,duration: 0.5}} className="h-[400px]" // Altezza fissa per uniformità
              >
                <ServiceCard title={service.title} description={service.description} icon={service.icon} buttonText={service.buttonText} buttonLink={service.buttonLink} bgColor={service.bgColor} delay={0.2 + index * 0.1} imageSrc={service.imageSrc} customButtons={service.customButtons} hasVideo={service.hasVideo} onVideoClick={service.onVideoClick} />
              </motion.div>
            ))}
            {/* Seconda riga: 2 box centrati con stessa altezza */}
            <div className="lg:col-span-3 flex justify-center gap-6">
              {services.slice(3).map((service,index)=> (
                <motion.div key={service.id} initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.2 + (index + 3) * 0.1,duration: 0.5}} className="w-full lg:w-[calc(33.333%-0.5rem)] h-[400px]" // Stessa larghezza e altezza dei box superiori
                >
                  <ServiceCard title={service.title} description={service.description} icon={service.icon} buttonText={service.buttonText} buttonLink={service.buttonLink} bgColor={service.bgColor} delay={0.2 + (index + 3) * 0.1} imageSrc={service.imageSrc} customButtons={service.customButtons} hasVideo={service.hasVideo} onVideoClick={service.onVideoClick} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <InteractiveDashboardShowcase />

      {/* Assessment Tools Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-2xl p-10 mb-16 border border-gray-100">
            <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="text-center mb-16" >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language==='en' ? 'COSMO Assessment Tools' : 'Strumenti di Valutazione COSMO'}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {language==='en' ? 'Discover our comprehensive personality assessment toolkit designed for clinical excellence.' : 'Scopri il nostro toolkit di valutazione della personalità completo progettato per l\'eccellenza clinica.'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {toolCategories.map((category,index)=> (
                <ToolCategoryCard key={index} title={category.title} description={category.description} icon={category.icon} content={category.content} delay={0.2 + index * 0.1} imageSrc={category.imageSrc} hasVideo={category.hasVideo} tooltip={category.tooltip} onVideoClick={category.hasVideo ? (
                  category.title.includes('Self Report') || category.title.includes('Autovalutazione')
                    ? ()=> setShowSelfReportVideoModal(true)
                    : ()=> setShowTypicalPerformanceVideoModal(true)
                ) : null} />
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center py-8">
            <a href="https://cosmo-self-report-se-absv.bolt.host" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl mb-3" >
              <FaChartLine className="mr-3" />
              {language==='en' ? 'Try our Tools' : 'Prova i Nostri Strumenti'}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <GPTSection />
      </div>

      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{duration: 0.5}} className="max-w-3xl mx-auto" >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language==='en' ? 'Ready to enhance your professional practice?' : 'Pronto a migliorare la tua pratica professionale?'}
            </h2>
            <p className="text-xl mb-8">
              {language==='en' ? 'Explore our platform and discover how COSMO can transform your approach to personality assessment and therapy.' : 'Esplora la nostra piattaforma e scopri come COSMO può trasformare il tuo approccio alla valutazione della personalità e alla terapia.'}
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a href="https://cosmo-self-report-se-absv.bolt.host" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white text-indigo-700 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg" >
                {language==='en' ? 'Try our Tools' : 'Prova i Nostri Strumenti'}
              </a>
              <button onClick={()=> handleNavigation("/contact")} className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-bold hover:bg-white/10 transition-colors" >
                {language==='en' ? 'Contact Us' : 'Contattaci'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Spostata la sezione FAQ alla fine, sotto la CTA */}
      <FAQSection />

      <VideoModal isOpen={showVideoModal} onClose={()=> setShowVideoModal(false)} videoId={language==='en' ? "OqWqpIS5WwE" : "RodsYAJH-gY"} />
      <DashboardVideoModal isOpen={showDashboardVideoModal} onClose={()=> setShowDashboardVideoModal(false)} />
      <SelfReportVideoModal isOpen={showSelfReportVideoModal} onClose={()=> setShowSelfReportVideoModal(false)} />
      <TypicalPerformanceVideoModal isOpen={showTypicalPerformanceVideoModal} onClose={()=> setShowTypicalPerformanceVideoModal(false)} />
      <ConsultationVideoModal isOpen={showConsultationVideoModal} onClose={()=> setShowConsultationVideoModal(false)} />
    </div>
  );
};

export default LandingPage;